import { Flex, Stack } from '@mantine/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useSimpleEpics } from '../../../api/tasks-client/tasks-client.hooks';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '../../../components/overview-card/overview-card';
import { safeParseDateInput } from '../../../helpers/date-time-helpers';
import { skyDark } from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';

export const EpicsOverview = () => {
  const i18n = useTranslation();
  const { epics: simpleEpics } = useSimpleEpics();

  // Memoized metric calculations
  const totalEpics = useMemo(() => simpleEpics?.length || 0, [simpleEpics]);

  const epicsWithUpcomingDueDate = useMemo(() => {
    if (!simpleEpics) return 0;
    const today = dayjs();
    return simpleEpics.filter((epic) => {
      if (!epic.due_date) return false;
      const dueDate = safeParseDateInput(epic.due_date);
      if (!dueDate) return false;
      return dueDate.isAfter(today) || dueDate.isSame(today);
    }).length;
  }, [simpleEpics]);

  const epicsCreatedLast90Days = useMemo(() => {
    if (!simpleEpics) return 0;
    const ninetyDaysAgo = dayjs().subtract(90, 'days');

    return simpleEpics.filter((epic) => {
      if (!epic.created_date) return false;
      const createdDate = safeParseDateInput(epic.created_date);
      if (!createdDate) return false;
      return createdDate.isAfter(ninetyDaysAgo) || createdDate.isSame(ninetyDaysAgo);
    }).length;
  }, [simpleEpics]);

  const epicsDueNext90Days = useMemo(() => {
    if (!simpleEpics) return 0;
    const today = dayjs();
    const ninetyDaysFromNow = dayjs().add(90, 'days');

    return simpleEpics.filter((epic) => {
      if (!epic.due_date) return false;
      const dueDate = safeParseDateInput(epic.due_date);
      if (!dueDate) return false;
      return dueDate.isAfter(today) || (dueDate.isSame(today) && dueDate.isBefore(ninetyDaysFromNow));
    }).length;
  }, [simpleEpics]);
  return (
    <Flex direction="row" gap={24}>
      <OverviewCard
        title="Total"
        icon="list_alt"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{totalEpics}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {`Total ${i18n.t('common.epics')}`}
            </Text>
          </Stack>
        }
      />
      <OverviewCard
        title="Recent"
        icon="fiber_new"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{epicsCreatedLast90Days}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              Created in Last 90 Days
            </Text>
          </Stack>
        }
      />
      <OverviewCard
        title="Scheduled"
        icon="event"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{epicsWithUpcomingDueDate}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {`${i18n.t('common.epics')} with Future Due Date`}
            </Text>
          </Stack>
        }
      />

      <OverviewCard
        title="Upcoming"
        icon="schedule"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{epicsDueNext90Days}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              Due in Next 90 Days
            </Text>
          </Stack>
        }
      />
    </Flex>
  );
};
